import _defineProperty from "@babel/runtime/helpers/defineProperty";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import { types, getParent, getRoot, cast } from 'mobx-state-tree';
import { ALLOCATION_METHOD } from 'src/constants/utilities';
import { SubmeterBillFinalStateModel } from './property/allocations';
const Period = types.model({
  allocationCharge: types.number,
  allocationId: types.string,
  occupancyFactor: types.number,
  periodEndDateOnly: types.string,
  periodStartDateOnly: types.string,
  residentShare: types.number
});
const Allocation = types.model({
  allocationCharge: types.number,
  allocationId: types.string,
  billingCycleTotal: types.maybeNull(types.number),
  correction: types.maybeNull(types.number),
  commonAreaDeduction: types.maybeNull(types.number),
  daysOccupied: types.number,
  isBillable: types.boolean,
  occupancyFactor: types.number,
  occupantsCount: types.number,
  periods: types.array(Period),
  proRataFactor: types.number,
  proratedOccupancyFactor: types.number,
  residentAccountId: types.maybeNull(types.string),
  residentId: types.maybeNull(types.string),
  unitName: types.string,
  unitOccupancyFactor: types.number
});
const Bill = types.model({
  amount: types.maybe(types.number),
  endDateOnly: types.maybe(types.string),
  fileId: types.maybeNull(types.string),
  redactedFileId: types.maybeNull(types.string),
  goodwillDeduction: types.maybe(types.number),
  id: types.maybe(types.string),
  isSkipped: types.maybe(types.boolean),
  isShared: types.maybe(types.maybeNull(types.boolean)),
  startDateOnly: types.maybe(types.string),
  utilityId: types.maybe(types.string)
}).actions(store => {
  return {
    changeRedactedFileId(data) {
      store.redactedFileId = data;
    },
    changeIsSharedFile(data) {
      store.isShared = data;
    },
    changeFileId(data) {
      store.fileId = data;
    }
  };
});
const AllocationsComparison = types.model({
  activeUnits: types.number,
  allocatedTotal: types.number,
  billId: types.maybeNull(types.string),
  billValue: types.maybeNull(types.number),
  commonAreaDeduction: types.number,
  goodwillDeduction: types.maybeNull(types.number),
  month: types.number,
  serviceEndDateOnly: types.maybeNull(types.string),
  serviceStartDateOnly: types.maybeNull(types.string),
  totalBilledToResident: types.number,
  totalOccupants: types.number,
  year: types.number
});
const Comparison = types.model({
  allocations: types.array(AllocationsComparison),
  companyId: types.string,
  currentBillValue: types.maybeNull(types.number),
  isBillIncreasedBy20Percent: types.maybeNull(types.boolean),
  isReviewed: types.maybeNull(types.boolean),
  lastBillValue: types.maybeNull(types.number),
  propertyId: types.string,
  utilityId: types.string
});
const AllocationUtility = types.model({
  allocated: types.number,
  allocationMethod: types.string,
  allocations: types.optional(types.array(Allocation), []),
  bill: types.maybeNull(Bill),
  billStatus: types.maybeNull(types.string),
  billTotal: types.number,
  bills: types.optional(types.array(Bill), []),
  commonAreaDeduction: types.number,
  comparison: types.maybeNull(Comparison),
  deductions: types.number,
  defaultGoodwillDeduction: types.number,
  isApproved: types.boolean,
  isEditable: types.boolean,
  isReviewCheckedValue: types.optional(types.boolean, false),
  isSkipped: types.boolean,
  ratioBilledTotal: types.number,
  recovery: types.number,
  utilityId: types.string,
  utilityType: types.maybeNull(types.string)
}).actions(store => {
  return {
    setReviewChecked(value) {
      try {
        store.isReviewCheckedValue = value;
      } catch (error) {
        console.error('STORE ERROR > ', error);
      }
    },
    setComparisonAfterFetch(data) {
      try {
        store.comparison = data.response;
      } catch (error) {
        console.error('STORE ERROR > ', error);
      }
    }
  };
}).views(store => {
  return {
    get isReviewChecked() {
      return store.isReviewCheckedValue;
    }
  };
});
const Preview = types.model('Preview', {
  allocated: types.maybeNull(types.number),
  additionalSkipComment: types.maybeNull(types.string),
  allocationMethod: types.string,
  allocationTableSkipReason: types.maybeNull(types.string),
  billAmount: types.maybeNull(types.number),
  billEndDateOnly: types.maybeNull(types.string),
  billStartDateOnly: types.maybeNull(types.string),
  billingCycleId: types.maybeNull(types.string),
  billStatus: types.string,
  deductions: types.maybeNull(types.number),
  fileId: types.maybeNull(types.string),
  isEditable: types.boolean,
  isSkipped: types.boolean,
  otherSkipReason: types.maybeNull(types.string),
  redactedFileId: types.maybeNull(types.string),
  utilityId: types.string,
  submeterFinalState: types.maybeNull(types.array(SubmeterBillFinalStateModel))
}).actions(store => {
  return {
    changeRedactedFileId(data) {
      store.redactedFileId = data;
    },
    changeFileId(data) {
      store.fileId = data;
    },
    updateSubmeterFinalState(data) {
      if (Array.isArray(data) && !!(data !== null && data !== void 0 && data.length)) {
        store.submeterFinalState = data;
      } else {
        store.submeterFinalState = null;
      }
    },
    fetchSubmeterFinalState(_ref) {
      let billingCycleId = _ref.billingCycleId;
      const parentPropertyObj = getParent(getParent(getParent(getParent(getParent(store)))));
      if (store.allocationMethod === ALLOCATION_METHOD.BySubmeter) {
        var _getRoot, _store$billingCycleId, _store$billingCycleId2;
        return (_getRoot = getRoot(store)) === null || _getRoot === void 0 ? void 0 : _getRoot.allocations.postGetSubMeterFinalStateAsync.run({
          // allocationMethod: store.allocationMethod,
          utilityId: store.utilityId,
          propertyId: parentPropertyObj.id,
          billingCycleId: (_store$billingCycleId = store.billingCycleId) !== null && _store$billingCycleId !== void 0 ? _store$billingCycleId : billingCycleId
        }, [store.utilityId, (_store$billingCycleId2 = store.billingCycleId) !== null && _store$billingCycleId2 !== void 0 ? _store$billingCycleId2 : billingCycleId, parentPropertyObj.id]).then(response => {
          if (response !== null && response !== void 0 && response.isSuccessful) {
            store.updateSubmeterFinalState(response.response);
          }
        });
      } else {
        return new Promise(resolve => {
          setTimeout(() => {
            resolve(null);
          }, 1);
        });
      }
    }
  };
});
const BillingCycleModel = types.model('BillingCycleModel', {
  isDefaultBillingCycle: types.boolean,
  isHistoricalAllocationCycle: types.boolean,
  utilityTables: types.array(Preview)
});
export const CycleChargesPreview = types.model({
  residentAccountId: types.maybeNull(types.string),
  residentId: types.maybeNull(types.string),
  totalAmount: types.number,
  unitId: types.maybeNull(types.string),
  unitName: types.maybeNull(types.string),
  utilityItems: types.array(types.model({
    amount: types.number,
    utilityId: types.maybeNull(types.string)
  }))
});
export const BillingSummary = types.model('BillingSummary', {
  allocatedTotal: types.number,
  allocationCycleId: types.string,
  billingCycleSummaries: types.array(types.model({
    allocatedTotal: types.number,
    billingCycleId: types.string,
    billsTotal: types.number,
    isDefaultBillingCycle: types.boolean
  })),
  billsTotal: types.number,
  cycleChargesPreview: types.optional(types.array(CycleChargesPreview), []),
  cycleName: types.string,
  cycleStatus: types.maybeNull(types.string),
  cycleUtilityTableStatuses: types.string,
  isCurrentAllocationCycle: types.boolean,
  month: types.number,
  propertyId: types.string,
  statuses: types.array(types.model({
    billStatus: types.string,
    count: types.number
  })),
  year: types.number
});
export const DateRange = types.model('DateRange', {
  endDateOnly: types.maybeNull(types.number),
  startDateOnly: types.maybeNull(types.number)
});
const TempFileData = types.model('TempFileData', {
  fileId: types.maybeNull(types.string),
  isShared: types.maybeNull(types.boolean),
  isReducted: types.maybeNull(types.boolean),
  isDeleted: types.maybeNull(types.boolean),
  billAutomationResponse: types.maybeNull(types.frozen())
});
const BillAllocation = types.model({
  billId: types.maybeNull(types.string),
  billStatus: types.maybeNull(types.string),
  billingCycleId: types.maybeNull(types.string),
  billValue: types.maybeNull(types.number),
  commonAreaDeduction: types.maybeNull(types.number),
  goodwillDeduction: types.maybeNull(types.number),
  rationBilled: types.maybeNull(types.number),
  serviceEndDateOnly: types.maybeNull(types.string),
  serviceStartDateOnly: types.maybeNull(types.string),
  submetered: types.maybeNull(types.number),
  total: types.maybeNull(types.number),
  utilityId: types.maybeNull(types.string)
});
const AllocationHistory = types.model('AllocationHistory', {
  allocationCycleId: types.maybeNull(types.string),
  month: types.maybeNull(types.number),
  propertyId: types.maybeNull(types.string),
  utilities: types.optional(types.array(BillAllocation), []),
  year: types.maybeNull(types.number),
  ym: types.maybeNull(types.number)
});
const AllocationsModel = types.model('AllocationsModel', {
  billingCycleIds: types.map(types.array(types.string)),
  billingCycles: types.map(BillingCycleModel),
  billingSummaryValue: types.maybeNull(types.array(BillingSummary)),
  comparisonById: types.map(Comparison),
  cycleChargesPreviewByAllocationId: types.map(types.array(CycleChargesPreview)),
  dateRangeValue: types.optional(DateRange, {
    endDateOnly: null,
    startDateOnly: null
  }),
  filterStatusesValue: types.optional(types.array(types.string), ['all']),
  statusValue: types.optional(types.string, 'all'),
  utilityById: types.map(AllocationUtility),
  allocationHistory: types.optional(types.array(AllocationHistory), []),
  tempFileData: types.optional(TempFileData, {
    fileId: null,
    isShared: null,
    isDeleted: null,
    isReducted: null,
    billAutomationResponse: null
  })
}).actions(store => {
  return {
    deleteCycle(id) {
      try {
        store.billingCycles.delete(id);
      } catch (error) {
        console.error('STORE ERROR > ', error);
      }
    },
    removeIdFromBillingCycleIds(allocationCycleId, id) {
      try {
        const ids = store.billingCycleIds.get(allocationCycleId);
        if (ids) {
          const index = ids.indexOf(id);
          if (index > -1) {
            ids.splice(index, 1);
            store.billingCycleIds.set(allocationCycleId, ids);
          }
        }
      } catch (error) {
        console.error('STORE ERROR > ', error);
      }
    },
    resetTempFileData() {
      store.tempFileData.fileId = null;
      store.tempFileData.isShared = null;
      store.tempFileData.isDeleted = null;
      store.tempFileData.isReducted = null;
      store.tempFileData.billAutomationResponse = null;
    },
    setBillingCycleIds(allocationCycleId, data) {
      try {
        store.billingCycleIds.set(allocationCycleId, data);
      } catch (error) {
        console.error('STORE ERROR > ', error);
      }
    },
    setTempFileData(key, value) {
      store.tempFileData[key] = value;
    },
    setBillingSummaryAfteFetch(data) {
      try {
        store.billingSummaryValue = data;
      } catch (error) {
        console.error('STORE ERROR > ', error);
      }
    },
    setChargesPreviewAfterFetch(id, data) {
      try {
        store.cycleChargesPreviewByAllocationId.set(id, data);
      } catch (error) {
        console.error('STORE ERROR > ', error);
      }
    },
    setAllocationHistory(data) {
      try {
        store.allocationHistory = cast([]);
        store.allocationHistory = cast(data);
      } catch (error) {
        console.error('STORE ERROR > ', error);
      }
    },
    setComparisonByIdAfterFetch(id, data) {
      try {
        store.comparisonById.set(id, data.response);
      } catch (error) {
        console.error('STORE ERROR > ', error);
      }
    },
    setCycleAfterFetch(id, data) {
      try {
        store.billingCycles.set(id, _objectSpread(_objectSpread({}, data.response), {}, {
          billingCycleId: id
        }));
      } catch (error) {
        console.error('STORE ERROR > ', error);
      }
    },
    setDateRange(data) {
      try {
        store.dateRangeValue = data;
      } catch (error) {
        console.error('STORE ERROR > ', error);
      }
    },
    setFilterStatuses(value) {
      try {
        store.filterStatusesValue = value;
      } catch (error) {
        console.error('STORE ERROR > ', error);
      }
    },
    setStatus(value) {
      try {
        store.statusValue = value;
      } catch (error) {
        console.error('STORE ERROR > ', error);
      }
    },
    setUtilityAfterFetch(billingCycleId, utilityId, data) {
      try {
        if (data.response.utilityTables.length > 0) store.utilityById.set(billingCycleId + utilityId, data.response.utilityTables.find(utility => utility.utilityId === utilityId));
      } catch (error) {
        console.error('STORE ERROR > ', error);
      }
    }
  };
}).views(store => {
  return {
    get billingSummary() {
      return store.billingSummaryValue;
    },
    get dateRange() {
      return store.dateRangeValue;
    },
    get filterStatuses() {
      return store.filterStatusesValue;
    },
    get status() {
      return store.statusValue;
    }
  };
});
export default AllocationsModel;